import { Injectable } from '@angular/core';
import * as THREE from 'three';
import mapboxgl from 'mapbox-gl';
import { Being } from '../../interfaces/being.model';
import { BeingService } from './being.service';
import { CustomLayerWithZoom, RenderedBeing } from '../../interfaces/master-interfaces';

/**
 * Renders beings as slowly tumbling polyhedra inside the map's own WebGL
 * context via Mapbox's CustomLayerInterface. Unlike a DOM Marker + CSS cube,
 * meshes here inherit the camera: they tilt with pitch, sit on terrain, and
 * are occluded correctly. One layer renders all beings.
 *
 * Data stays in BeingService; this service owns pixels only.
 */

@Injectable({ providedIn: 'root' })



export class BeingRendererService {

  private readonly MESH_MINZOOM = 12;  // City-level zoom and closer
  private readonly LAYER_ID = 'being-polyhedra';
  /** Physical radius of a being, metres. Scaled for overhead visibility. */
  private readonly RADIUS_M = 2.5;        // ~SUV half-length
  private readonly HOVER_M = 2.5;         // gap between ground and belly
  private readonly MIN_PIXEL_RADIUS = 10;  // screen-size floor when far       // physical radius once you're close
  private map: mapboxgl.Map | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.Camera();
  private renderFrameCallCount = 0;
  private rendered = new Map<string, RenderedBeing>();
  private lastFrameTime = 0;
  private shadowGeometry = new THREE.PlaneGeometry(2, 2); // XY plane, faces +Z (up) — already flat on ground
  private shadowTexture = this.makeShadowTexture();
  private beingShadows = new Map<string, THREE.Mesh>();
  // Shared geometry — one icosahedron for every being, materials vary.
  private geometry = new THREE.IcosahedronGeometry(1, 0);
  private beingTrails = new Map<string, THREE.Line>();
  private trailPointsMap = new Map<string, Array<{ lng: number; lat: number; age: number }>>();
  private lastGoodElevation = new Map<string, number>();
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private attachCount = 0;

  constructor(
    private _beingService: BeingService
  ) {
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const sun = new THREE.DirectionalLight(0xffffff, 1.4);
    sun.position.set(0.5, 0.8, 1);
    this.scene.add(sun);
  }

  // ---------------------------------------------------------------
  // Layer lifecycle
  // ---------------------------------------------------------------

  /** Call once after `map.on('style.load')`. Safe to call again after style swaps. */
  attachToMap(map: mapboxgl.Map): void {
    // console.log('📍 attachToMap() called, style loaded?', map.isStyleLoaded());
    // console.log('🔗 attachToMap #' + (++this.attachCount), 'sameMap:', this.map === map);
    //console.log('🔗 attachToMap', new Error().stack)
    // Clean up any old layers/sources from prior attachments
    if (map.getLayer('beings-layer')) map.removeLayer('beings-layer');
    if (map.getSource('beings-source')) map.removeSource('beings-source');
    if (map.getLayer('being-dots')) map.removeLayer('being-dots');
    if (map.getSource('being-dots-source')) map.removeSource('being-dots-source');

    this.map = map;
    if (map.getLayer(this.LAYER_ID)) {
      // console.log('⚠️  Layer already exists, skipping attach');
      return;
    }

    const layer: CustomLayerWithZoom = {
      id: this.LAYER_ID,
      type: 'custom',
      renderingMode: '3d',
      minzoom: Math.max(this.MESH_MINZOOM, this._beingService.getVisibilityThreshold()),
      onAdd: (m, gl) => {
        const canvas = gl.canvas;
        if (canvas instanceof HTMLCanvasElement) {
          const width = canvas.clientWidth;
          const height = canvas.clientHeight;
          canvas.width = width;
          canvas.height = height;
        }

        this.renderer = new THREE.WebGLRenderer({
          context: gl,
          canvas,
          antialias: true,
          alpha: true,
        });
        this.renderer.autoClear = false;
        this.renderer.setPixelRatio(window.devicePixelRatio);

        // tell Three.js to render only within the canvas bounds
        gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
        gl.scissor(0, 0, gl.canvas.width, gl.canvas.height);
        gl.enable(gl.SCISSOR_TEST);
      },
      render: (_gl, matrix) => this.renderFrame(matrix as number[]),
      onRemove: () => {
        this.renderer = null;   // don't dispose() — the GL context is Mapbox's
      }
    };

    // console.log('🔨 Calling map.addLayer()');
    map.addLayer(layer as mapboxgl.CustomLayerInterface);
    // console.log('✅ map.addLayer() returned, layer ID:', this.LAYER_ID);
  }

  // ---------------------------------------------------------------
  // Sync with simulation
  // ---------------------------------------------------------------

  removeBeing(id: string): void {
    const r = this.rendered.get(id);
    if (!r) return;
    // console.warn('🗑️ REMOVE', id.slice(0, 8), new Error().stack);
    this.scene.remove(r.mesh);
    this.disposeMesh(r.mesh);
    this.rendered.delete(id);

    const trail = this.beingTrails.get(id);
    if (trail) {
      this.scene.remove(trail);
      trail.geometry.dispose();
      (trail.material as THREE.Material).dispose();
      this.beingTrails.delete(id);
    }
    this.trailPointsMap.delete(id);

    const shadow = this.beingShadows.get(id);
    if (shadow) {
      this.scene.remove(shadow);
      (shadow.material as THREE.Material).dispose();
      this.beingShadows.delete(id);
    }
    this.lastGoodElevation.delete(id);
    this.map?.triggerRepaint();
  }

  /**
   * Diff the current being list against rendered meshes.
   * Wire to getMyBeings()/getCreatedBeings() — cheap enough to call on every emit.
   */
  syncBeings(beings: Being[], allBeings: Being[]): void {
    // console.log(`🔄 syncBeings called with ${beings.length} beings, allBeings: ${allBeings.length}, currently rendered: ${this.rendered.size}`);
    // console.log('sync', beings.length, allBeings.length, allBeings === beings ? '⚠️ ONE-ARG CALL' : '');
    const incoming = new Set<string>();

    for (const being of allBeings) {
      incoming.add(being.id);
      const existing = this.rendered.get(being.id);

      if (existing) {
        existing.lng = being.position.lng;
        existing.lat = being.position.lat;
        existing.alive = being.body.alive;
        // console.log(`✏️  Updated being ${being.id.slice(0, 8)}: alive=${being.body.alive}`);
      } else {
        // console.log(`✨ Adding being ${being.id.slice(0, 8)}`);
        this.addBeing(being);
      }
    }

    // Check for deletions
    let removedCount = 0;
    for (const [id, r] of this.rendered) {
      if (!incoming.has(id)) {
        this.scene.remove(r.mesh);
        this.disposeMesh(r.mesh);
        this.rendered.delete(id);
        removedCount++;

        const trail = this.beingTrails.get(id);
        if (trail) {
          this.scene.remove(trail);
          trail.geometry.dispose();
          (trail.material as THREE.Material).dispose();
          this.beingTrails.delete(id);
        }
        this.trailPointsMap.delete(id);

        const shadow = this.beingShadows.get(id);
        if (shadow) {
          this.scene.remove(shadow);
          (shadow.material as THREE.Material).dispose();
          this.beingShadows.delete(id);
        }
        this.lastGoodElevation.delete(id);
      }
    }

    // console.log(`✅ syncBeings done: added 0, removed ${removedCount}, rendered count now: ${this.rendered.size}`);
    this.map?.triggerRepaint();
  }

  upsertBeing(being: Being): void {
    const existing = this.rendered.get(being.id);
    if (existing) {
      existing.alive = being.body.alive;
      // note: do NOT overwrite lng/lat here — see #3
    } else {
      this.addBeing(being);
    }
    this.map?.triggerRepaint();
  }

  private addBeing(being: Being): void {

    const lng = being.position?.lng, lat = being.position?.lat;
    if (!Number.isFinite(lng) || !Number.isFinite(lat) || (lng === 0 && lat === 0)) {
      // console.warn('⏭️ skipping being with no valid position', being.id.slice(0, 8));
      return;   // next syncBeings pass will add it once position is real
    }

    //  console.log('✨ Adding being:', being.id);

    const baseColour = this.colourFromGenome(being);

    const material = new THREE.MeshStandardMaterial({
      color: baseColour.clone(),
      emissive: baseColour.clone(),
      emissiveIntensity: 0.3,
      flatShading: true,
      roughness: 0.3,
      metalness: 0.0
    });

    const mesh = new THREE.Mesh(this.geometry, material);
    mesh.frustumCulled = false;  // Prevent culling in 3D projection
    this.scene.add(mesh);

    const trailGeometry = new THREE.BufferGeometry();
    const trailMaterial = new THREE.LineBasicMaterial({
      color: baseColour.clone(),
      linewidth: 2,
      transparent: true,
      fog: false
    });
    const trailLine = new THREE.Line(trailGeometry, trailMaterial);
    trailLine.frustumCulled = false;
    this.scene.add(trailLine);
    this.beingTrails.set(being.id, trailLine);
    this.trailPointsMap.set(being.id, []);

    const shadowMat = new THREE.MeshBasicMaterial({
      map: this.shadowTexture,
      transparent: true,
      depthWrite: false,
      depthTest: false      // was true
    });
    const shadow = new THREE.Mesh(this.shadowGeometry, shadowMat);
    shadow.renderOrder = -1;   // draw before the beings so they sit on top
    shadow.frustumCulled = false;
    this.scene.add(shadow);
    this.beingShadows.set(being.id, shadow);

    this.rendered.set(being.id, {
      mesh,
      spin: this.spinFromGenome(being),
      lng: being.position.lng,
      lat: being.position.lat,
      alive: being.body.alive,
      bobPhase: this.hashToUnit(being.id) * Math.PI * 2,
      baseColour
    });
  }

  /** Called from simulation or position updates */
  updatePosition(beingId: string, lat: number, lng: number): void {
    const r = this.rendered.get(beingId);
    if (!r) return;
    r.lat = lat;
    r.lng = lng;

    // Add to trail
    const trail = this.trailPointsMap.get(beingId);
    if (trail) {
      trail.push({ lng, lat, age: 0 });
      // Keep last 100 points
      if (trail.length > 100) trail.shift();
    }

    this.map?.triggerRepaint();
  }



  /** Being nearest to a click point, or null. `point` is mapbox's canvas-relative e.point. */
  beingAt(point: { x: number; y: number }): string | null {
    if (!this.map) return null;

    const zoom = this.map.getZoom();
    if (zoom < this.MESH_MINZOOM) return null;   // nothing is drawn down here

    const lat = this.map.getCenter().lat;
    const mpp = this.metresPerPixel(zoom, lat);
    const pxRadius = Math.max(14, this.screenRadiusMetres(zoom, lat) / mpp);

    let best: string | null = null;
    let bestDist = pxRadius * pxRadius;

    for (const [id, r] of this.rendered) {
      const p = this.map.project([r.lng, r.lat]);
      const dx = p.x - point.x;
      const dy = p.y - point.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) {
        bestDist = d2;
        best = id;
      }
    }

    return best;
  }

  private metresPerPixel(zoom: number, lat: number): number {
    return 40075016.686 * Math.cos(lat * Math.PI / 180) / (512 * Math.pow(2, zoom));
  }


  /** World-space radius a being is drawn at. Shared by renderFrame and hit-testing. */
  private screenRadiusMetres(zoom: number, lat: number): number {
    return Math.max(this.RADIUS_M, this.MIN_PIXEL_RADIUS * this.metresPerPixel(zoom, lat));
  }

  private renderFrame(matrix: number[]): void {
    this.renderFrameCallCount++;

    // Timing first — before any early return — or dt accumulates while gated
    // and the tumble snaps through several revolutions on re-entry.
    const now = performance.now();
    const dt = Math.min(this.lastFrameTime ? (now - this.lastFrameTime) / 1000 : 0, 0.1);
    this.lastFrameTime = now;

    if (!this.map || !this.renderer || this.rendered.size === 0) return;

    const zoom = this.map.getZoom();
    if (zoom < this.MESH_MINZOOM) return;   // custom layers ignore minzoom

    const gl = this.renderer.getContext();
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);

    const centre = this.map.getCenter();
    const anchor = mapboxgl.MercatorCoordinate.fromLngLat(centre, 0);
    const metre = anchor.meterInMercatorCoordinateUnits();

    // World radius that holds a roughly constant on-screen size when far away,
    // settling to true physical size once you're close enough for it to matter.
    const screenRadius = this.screenRadiusMetres(zoom, centre.lat);
    const hover = screenRadius * 0.4;   // proportional gap, reads the same at every zoom

    for (const [beingId, r] of this.rendered.entries()) {

      if (this.renderFrameCallCount % 120 === 0) {
        // console.log(beingId.slice(0, 8), 'lngLat', r.lng, r.lat, 'alive', r.alive,
        //   'pos', r.mesh.position.x.toFixed(1), r.mesh.position.y.toFixed(1), r.mesh.position.z.toFixed(1));
      }

      // --- ground ---------------------------------------------------
      const raw = this.map.queryTerrainElevation({ lng: r.lng, lat: r.lat });
      if (raw != null) this.lastGoodElevation.set(beingId, raw);
      const ground = raw ?? this.lastGoodElevation.get(beingId) ?? 0;

      // --- body -----------------------------------------------------
      const bob = r.alive
        ? Math.sin(now / 1000 * 1.2 + r.bobPhase) * hover * 0.2
        : 0;

      // Centre sits one radius + hover above ground, so the belly clears the surface.
      const alt = ground + screenRadius + hover + bob;

      const merc = mapboxgl.MercatorCoordinate.fromLngLat({ lng: r.lng, lat: r.lat }, alt);
      r.mesh.position.set(
        (merc.x - anchor.x) / metre,
        -(merc.y - anchor.y) / metre,
        (merc.z - anchor.z) / metre
      );
      r.mesh.scale.setScalar(screenRadius * (r.alive ? 1 : 0.5));

      if (this.renderFrameCallCount % 120 === 0) {
        // console.log(beingId.slice(0, 8), 'lngLat', r.lng.toFixed(4), r.lat.toFixed(4),
        //   'alive', r.alive, '→ pos', r.mesh.position.x.toFixed(1),
        //   r.mesh.position.y.toFixed(1), r.mesh.position.z.toFixed(1));
      }
      if (r.alive) {
        r.mesh.rotation.x += r.spin.x * dt;
        r.mesh.rotation.y += r.spin.y * dt;
        r.mesh.rotation.z += r.spin.z * dt;
      }

      const mat = r.mesh.material as THREE.MeshStandardMaterial;
      if (r.alive) {
        mat.emissiveIntensity = 0.3;
        mat.color.copy(r.baseColour);
      } else {
        mat.emissiveIntensity = 0.02;
        mat.color.copy(r.baseColour).lerp(new THREE.Color(0x555555), 0.75);
      }

      // --- shadow ---------------------------------------------------
      const shadow = this.beingShadows.get(beingId);
      if (shadow) {
        const sMerc = mapboxgl.MercatorCoordinate.fromLngLat(
          { lng: r.lng, lat: r.lat },
          ground + screenRadius * 0.02        // hair above surface, avoids z-fighting
        );
        shadow.position.set(
          (sMerc.x - anchor.x) / metre,
          -(sMerc.y - anchor.y) / metre,
          (sMerc.z - anchor.z) / metre
        );
        const bobLift = hover > 0 ? bob / hover : 0;   // -0.2 … +0.2
        shadow.scale.setScalar(screenRadius * (1.25 - bobLift * 0.25));
        (shadow.material as THREE.MeshBasicMaterial).opacity = 0.9 - bobLift * 0.3;
        shadow.visible = r.alive;
      }

      this.updateTrailMesh(beingId, anchor, metre);
    }

    this.camera.projectionMatrix = new THREE.Matrix4()
      .fromArray(matrix)
      .multiply(
        new THREE.Matrix4()
          .makeTranslation(anchor.x, anchor.y, anchor.z)
          .scale(new THREE.Vector3(metre, -metre, metre))
      );

    this.renderer.resetState();
    this.renderer.render(this.scene, this.camera);

    this.map.triggerRepaint();
  }

  private updateTrailMesh(beingId: string, anchor: mapboxgl.MercatorCoordinate, metre: number): void {
    const trail = this.trailPointsMap.get(beingId);
    const trailLine = this.beingTrails.get(beingId);

    if (!trail || !trailLine || trail.length < 2) return;

    const positions: number[] = [];

    for (const point of trail) {
      const merc = mapboxgl.MercatorCoordinate.fromLngLat(
        { lng: point.lng, lat: point.lat },
        this.groundElevation(
          this.elevationKey(point.lng, point.lat),
          point.lng,
          point.lat
        ) + 0.5
      );

      positions.push(
        (merc.x - anchor.x) / metre,
        -(merc.y - anchor.y) / metre,
        (merc.z - anchor.z) / metre
      );
    }

    const geometry = trailLine.geometry as THREE.BufferGeometry;
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    geometry.computeBoundingSphere();

    // Fade trail opacity over time
    const material = trailLine.material as THREE.LineBasicMaterial;
    material.opacity = 0.6 * (1 - trail.length / 100); // Fades as it gets older
  }

  detach(): void {
    if (this.map?.getLayer(this.LAYER_ID)) {
      this.map.removeLayer(this.LAYER_ID);
    }
    this.rendered.forEach(r => this.disposeMesh(r.mesh));
    this.beingTrails.forEach(trail => {
      (trail.material as THREE.Material).dispose();
      trail.geometry.dispose();
    });
    this.beingShadows.forEach(s => {
      this.scene.remove(s);
      (s.material as THREE.Material).dispose();
    });
    this.beingShadows.clear();
    this.lastGoodElevation.clear();
    this.rendered.clear();
    this.beingTrails.clear();
    this.trailPointsMap.clear();
    this.map = null;
  }

  // ---------------------------------------------------------------
  // Frame loop
  // ---------------------------------------------------------------

  private groundElevation(key: string, lng: number, lat: number): number {
    const e = this.map?.queryTerrainElevation({ lng, lat });
    if (e != null) {
      this.lastGoodElevation.set(key, e);
      return e;
    }

    return this.lastGoodElevation.get(key) ?? 0;
  }

  private elevationKey(lng: number, lat: number): string {
    return `${lng.toFixed(4)},${lat.toFixed(4)}`;  // ~11 m grid
  }

  // ---------------------------------------------------------------
  // Genome → appearance
  // ---------------------------------------------------------------

  /** Lineage fixes the hue (families look related); sociability warms it up. */
  private colourFromGenome(being: Being): THREE.Color {
    const hue = this.hashToUnit(being.id);
    return new THREE.Color().setHSL(hue, 0.65, 0.55);
  }

  // /** Faster metabolism → faster tumble. ~0.2–0.8 rad/s stays "slow". */
  private spinFromGenome(being: Being): THREE.Vector3 {
    const axis = (salt: string) => 0.2 + this.hashToUnit(being.id + salt) * 0.6;  // 0.2–0.8 rad/s
    return new THREE.Vector3(axis('x'), axis('y'), axis('z'));
  }

  private hashToUnit(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (h * 31 + s.charCodeAt(i)) >>> 0;
    }
    return (h % 360) / 360;
  }

  private disposeMesh(mesh: THREE.Mesh): void {
    (mesh.material as THREE.Material).dispose();
    // geometry is shared — disposed only if the service is ever destroyed
  }


  private makeShadowTexture(): THREE.Texture {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const ctx = c.getContext('2d')!;
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    g.addColorStop(0.0, 'rgba(0,0,0,0.45)');
    g.addColorStop(0.6, 'rgba(0,0,0,0.25)');
    g.addColorStop(1.0, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    return new THREE.CanvasTexture(c);
  }
}